import React, { useState, useRef, useEffect } from 'react';
import { getVolume, speakText, setReplay, setHud } from './lzAudio';

// 10-level "What comes next?" pattern-reasoning game (ages 2-5).

const LEVELS = [
    { id: 1, type: 'AB', seq: ['🔴', '🔵', '🔴', '🔵', '🔴'], answer: '🔵', options: ['🔵', '🔴', '🟢'], difficulty: 'Easy', age: 'Age 2', hint: 'Red, blue, red, blue…', explain: 'Red, blue, red, blue — it takes turns! Blue comes next.' },
    { id: 2, type: 'AB', seq: ['🌞', '🌙', '🌞', '🌙', '🌞'], answer: '🌙', options: ['🌙', '🌞', '⭐'], difficulty: 'Easy', age: 'Age 2', hint: 'Day, night, day, night…', explain: 'Sun, moon, sun, moon — the moon comes after the sun!' },
    { id: 3, type: 'AB', seq: ['🐶', '🐱', '🐶', '🐱', '🐶'], answer: '🐱', options: ['🐱', '🐶', '🐰'], difficulty: 'Easy', age: 'Age 2–3', hint: 'Dog, cat, dog, cat…', explain: 'Dog, cat, dog, cat — the cat is next!' },
    { id: 4, type: 'ABC', seq: ['⭕', '🔷', '🔺', '⭕', '🔷'], answer: '🔺', options: ['🔺', '⭕', '🔷'], difficulty: 'Medium', age: 'Age 3', hint: 'Three shapes repeat!', explain: 'Circle, diamond, triangle — then it starts again. Triangle is next!' },
    { id: 5, type: 'ABC', seq: ['🍎', '🍊', '🍇', '🍎', '🍊'], answer: '🍇', options: ['🍇', '🍎', '🍌'], difficulty: 'Medium', age: 'Age 3', hint: 'Apple, orange, grapes…', explain: 'Apple, orange, grapes — grapes come after orange!' },
    { id: 6, type: 'ABB', seq: ['⭐', '🔵', '🔵', '⭐', '🔵', '🔵', '⭐'], answer: '🔵', options: ['🔵', '⭐', '🔴'], difficulty: 'Medium', age: 'Age 3–4', hint: 'One star, then two…', explain: 'One star, two blue circles — after a star comes blue!' },
    { id: 7, type: 'AABB', seq: ['🌸', '🌸', '🌼', '🌼', '🌸', '🌸', '🌼'], answer: '🌼', options: ['🌼', '🌸', '🌻'], difficulty: 'Hard', age: 'Age 4', hint: 'Everything comes in twos!', explain: 'Two pink, two yellow — the yellow flower needs its buddy!' },
    { id: 8, type: 'Growing', seq: ['🌱', '🌿', '🌳', '🌱', '🌿'], answer: '🌳', options: ['🌳', '🌱', '🌵'], difficulty: 'Hard', age: 'Age 4', hint: 'Small, bigger…', explain: 'Seed, plant, tree — it grows bigger each time! Tree is next.' },
    { id: 9, type: 'ABCD', seq: ['🐶', '🐱', '🐰', '🐟', '🐶', '🐱', '🐰'], answer: '🐟', options: ['🐟', '🐶', '🐸'], difficulty: 'Expert', age: 'Age 4–5', hint: 'Four friends take turns!', explain: 'Dog, cat, bunny, fish — the fish comes after the bunny!' },
    { id: 10, type: 'ABAC', seq: ['⭐', '🔴', '⭐', '🔵', '⭐', '🔴', '⭐'], answer: '🔵', options: ['🔵', '🔴', '⭐'], difficulty: 'Expert', age: 'Age 5', hint: 'Star, red, star, blue…', explain: 'Star, red, star, blue — last time was red, so now it is blue!' },
];

const DIFF_COLOR = { Easy: '#22C55E', Medium: '#F59E0B', Hard: '#F97316', Expert: '#8B5CF6' };
const ADVANCE_MS = 2200;

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};
const starsFor = (pct) => (pct >= 100 ? 3 : pct >= 70 ? 2 : pct >= 40 ? 1 : 0);

const FixThePattern = ({ childId, childAge, onComplete }) => {
    const [index, setIndex] = useState(0);
    const [options, setOptions] = useState(() => shuffle(LEVELS[0].options));
    const [results, setResults] = useState([]);
    const [picked, setPicked] = useState(null);
    const [showHint, setShowHint] = useState(false);
    const [finished, setFinished] = useState(false);

    const audioCtx = useRef(null);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);

    const tone = (freq, delayMs = 0, gain = 0.15, dur = 0.18, type = 'sine') => {
        const vol = getVolume();
        if (!vol) return;
        try {
            if (!audioCtx.current) {
                const Ctx = window.AudioContext || window.webkitAudioContext;
                if (!Ctx) return;
                audioCtx.current = new Ctx();
            }
            const ctx = audioCtx.current;
            if (ctx.state === 'suspended') ctx.resume();
            const t = ctx.currentTime + delayMs / 1000;
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = type;
            osc.frequency.value = freq;
            g.gain.setValueAtTime(gain * vol, t);
            g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
            osc.connect(g).connect(ctx.destination);
            osc.start(t);
            osc.stop(t + dur + 0.02);
        } catch (e) { /* ignore */ }
    };
    const playCorrect = () => [523, 659, 784].forEach((f, i) => tone(f, i * 120));
    const playWrong = () => tone(150, 0, 0.12, 0.3, 'sawtooth');

    const level = LEVELS[index];

    useEffect(() => {
        if (finished) return undefined;
        const t = setTimeout(() => speakText('What comes next?'), 400);
        return () => clearTimeout(t);
    }, [index, finished]);

    useEffect(() => {
        setReplay(() => (finished ? 'Great job! Tap play again for more patterns.' : picked !== null ? level.explain : showHint ? `What comes next? ${level.hint}` : 'What comes next?'));
        return () => setReplay(null);
    }, [finished, picked, showHint, level]);
    useEffect(() => {
        setHud({ score: results.filter(Boolean).length, total: LEVELS.length, question: 'What comes next? 🧐' });
        return () => setHud(null);
    }, [results]);

    const startLevel = (i) => {
        setIndex(i);
        setOptions(shuffle(LEVELS[i].options));
        setPicked(null);
        setShowHint(false);
    };

    const choose = (opt) => {
        if (picked !== null) return;
        const correct = opt === level.answer;
        setPicked(opt);
        if (correct) playCorrect(); else playWrong();
        speakText(`${correct ? 'Yes! ' : 'Good try! '}${level.explain}`);
        setResults((r) => [...r, correct]);
        onComplete?.(level.id, correct, level.type);
        timer.current = setTimeout(() => {
            if (index + 1 < LEVELS.length) startLevel(index + 1);
            else setFinished(true);
        }, ADVANCE_MS);
    };

    const replay = () => {
        clearTimeout(timer.current);
        setResults([]);
        setFinished(false);
        startLevel(0);
    };

    const correctCount = results.filter(Boolean).length;
    const font = { fontFamily: '"Baloo 2", "Fredoka One", "Comic Sans MS", system-ui, sans-serif' };

    const styles = `
        @keyframes fp-pulse { 0%,100% { border-color: #60A5FA; transform: scale(1) } 50% { border-color: #2563EB; transform: scale(1.06) } }
        @keyframes fp-pop { 0% { transform: scale(0) } 70% { transform: scale(1.2) } 100% { transform: scale(1) } }
        @keyframes fp-shake { 0%,100% { transform: translateX(0) } 25% { transform: translateX(-8px) } 75% { transform: translateX(8px) } }
        @keyframes fp-in { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
        .fp-opt { transition: transform .15s; }
        .fp-opt:not(:disabled):hover { transform: scale(1.08); }
    `;

    const dots = (
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
            {LEVELS.map((l, i) => {
                const r = results[i];
                const current = i === index && !finished;
                const bg = r === true ? '#22C55E' : r === false ? '#EF4444' : current ? DIFF_COLOR[l.difficulty] : '#E2E8F0';
                return <span key={l.id} style={{ width: current ? 16 : 12, height: current ? 16 : 12, borderRadius: '50%', background: bg, boxShadow: current ? `0 0 0 3px ${DIFF_COLOR[l.difficulty]}55` : 'none', transition: 'all .2s' }} />;
            })}
        </div>
    );

    if (finished) {
        const pct = Math.round((correctCount / LEVELS.length) * 100);
        const stars = starsFor(pct);
        return (
            <div style={{ ...font, padding: 16, textAlign: 'center', color: '#1E293B' }}>
                <style>{styles}</style>
                <div style={{ fontSize: 56 }}>{stars === 3 ? '🏆' : stars >= 1 ? '🎉' : '💪'}</div>
                <h3 style={{ fontWeight: 900, margin: '6px 0' }}>{stars === 3 ? 'Pattern master!' : stars >= 1 ? 'Great job!' : 'Good try!'}</h3>
                <div style={{ fontSize: 42, margin: '6px 0' }}>
                    {[1, 2, 3].map((s) => (
                        <span key={s} style={{ display: 'inline-block', opacity: s <= stars ? 1 : 0.25, filter: s <= stars ? 'none' : 'grayscale(1)', animation: s <= stars ? `fp-pop .4s ease ${s * 0.15}s both` : 'none' }}>⭐</span>
                    ))}
                </div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>You got {correctCount} out of {LEVELS.length} ({pct}%)</div>
                <div style={{ margin: '14px 0' }}>{dots}</div>
                <button type="button" onClick={replay} style={{ ...font, minHeight: 48, padding: '8px 28px', borderRadius: 24, border: 'none', background: '#2563EB', color: '#fff', fontWeight: 900, fontSize: 18, cursor: 'pointer' }}>🔄 Play again</button>
            </div>
        );
    }

    const answered = picked !== null;
    const wasCorrect = answered && picked === level.answer;
    const box = { width: 52, height: 52, minWidth: 52, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, background: '#fff', border: '3px solid #E2E8F0', boxSizing: 'border-box' };

    return (
        <div style={{ ...font, padding: 16, color: '#1E293B', maxWidth: 620, margin: '0 auto' }}>
            <style>{styles}</style>
            {dots}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 6px', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 800, color: '#475569' }}>Level {level.id} · {level.type} pattern · {level.age}</span>
                <span style={{ background: DIFF_COLOR[level.difficulty], color: '#fff', fontWeight: 800, fontSize: 12, padding: '3px 12px', borderRadius: 12 }}>{level.difficulty}</span>
            </div>

            {/* Sequence */}
            <div key={level.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', rowGap: 8, animation: 'fp-in .3s ease' }}>
                {level.seq.map((e, i) => (
                    <React.Fragment key={i}>
                        <div style={box}>{e}</div>
                        <span style={{ width: 8, height: 3, background: '#CBD5E1' }} />
                    </React.Fragment>
                ))}
                <div style={{
                    ...box,
                    border: answered ? `3px solid ${wasCorrect ? '#22C55E' : '#F59E0B'}` : '3px dashed #60A5FA',
                    background: answered ? (wasCorrect ? '#DCFCE7' : '#FEF3C7') : '#EFF6FF',
                    animation: answered ? 'fp-pop .35s ease' : 'fp-pulse 1.2s ease infinite',
                    color: '#2563EB', fontWeight: 900,
                }}>
                    {answered ? level.answer : '?'}
                </div>
            </div>

            {/* Options */}
            <div style={{ display: 'flex', gap: 18, justifyContent: 'center', margin: '24px 0 10px' }}>
                {options.map((opt) => {
                    const isPicked = picked === opt;
                    const isAnswer = opt === level.answer;
                    let border = '#E2E8F0';
                    let bg = '#fff';
                    if (answered && isAnswer) { border = '#22C55E'; bg = '#DCFCE7'; }
                    else if (isPicked) { border = '#EF4444'; bg = '#FEE2E2'; }
                    return (
                        <button
                            key={opt}
                            type="button"
                            className="fp-opt"
                            disabled={answered}
                            onClick={() => choose(opt)}
                            style={{
                                width: 116, height: 116, borderRadius: '50%', fontSize: 60, background: bg, border: `4px solid ${border}`,
                                cursor: answered ? 'default' : 'pointer', boxShadow: '0 4px 12px rgba(0,0,0,.1)',
                                animation: isPicked && !isAnswer ? 'fp-shake .4s ease' : 'none',
                            }}
                        >
                            {opt}
                        </button>
                    );
                })}
            </div>

            <div style={{ minHeight: 70, marginTop: 8, textAlign: 'center' }}>
                {answered ? (
                    <div style={{ padding: '10px 14px', borderRadius: 14, background: wasCorrect ? '#DCFCE7' : '#FEF3C7', border: `2px solid ${wasCorrect ? '#22C55E' : '#F59E0B'}`, fontWeight: 700, animation: 'fp-in .25s ease' }}>
                        <div style={{ fontSize: 18, fontWeight: 900 }}>{wasCorrect ? '✅ You found the pattern!' : '💡 Good try!'}</div>
                        <div>{level.explain}</div>
                    </div>
                ) : showHint ? (
                    <div style={{ padding: '10px 14px', borderRadius: 14, background: '#EFF6FF', border: '2px solid #93C5FD', fontWeight: 700 }}>💡 {level.hint}</div>
                ) : (
                    <button type="button" onClick={() => setShowHint(true)} style={{ ...font, minHeight: 44, padding: '6px 20px', borderRadius: 22, border: '2px solid #93C5FD', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 800, cursor: 'pointer' }}>💡 Hint</button>
                )}
            </div>

        </div>
    );
};

export default FixThePattern;
